import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, MapPin, Clock } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const EASE = [0.16, 1, 0.3, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const MILLS = [
  { name: "Loro Piana", origin: "Quarona, Italy", note: "Exceptionally fine worsteds and luxury blends with a soft, refined hand." },
  { name: "Cloth by Zegna", origin: "Trivero, Italy", note: "High-performance Italian wools with a consistent finish and elegant drape." },
  { name: "Holland & Sherry", origin: "Savile Row, England", note: "Traditional British suitings, formal cloths and evening fabrics." },
  { name: "Drago", origin: "Biella, Italy", note: "Super 160s luxury and Bluefeel collections — lightweight, resilient, elegant." },
  { name: "Dormeuil", origin: "England / France", note: "Classic English structure with refined luxury blends, woven with sustainable methods." },
  { name: "Stylbiella", origin: "Biella, Italy", note: "Contemporary Italian cloth with expressive colour and a modern, soft hand." },
  { name: "Vitale Barberis Canonico", origin: "Biella, Italy", note: "The oldest woollen mill in the world — superb worsteds since 1663." },
  { name: "Ariston", origin: "Naples, Italy", note: "Neapolitan cloth merchant with expressive patterns and rich colour." },
  { name: "Fratelli Tallia di Delfino", origin: "Biella, Italy", note: "Distinctive colour and pattern work with a luxurious finish." },
];

const SERVICES_LIST = [
  { id: "suits", name: "Bespoke & M2M Suits", tagline: "Two-piece and three-piece commissions, cut from a paper pattern drafted only for you.", time: "60 min consultation", img: "/images/client-grey-suit.jpg", contain: true },
  { id: "sport-coats", name: "Custom Sport Coats", tagline: "Unstructured cashmere, silk and linen blazers with Neapolitan softness.", time: "45 min consultation", img: "/images/sport-coat.jpg", cloth: "Stylbiella" },
  { id: "shirts-pants", name: "Shirts & Trousers", tagline: "Custom shirts in cloth from Albini, Alumo, Canclini and Thomas Mason — plus tailored trousers, made to your measure.", time: "30 min consultation", img: "/images/custom-shirt.png", contain: true },
  { id: "wedding", name: "Wedding Suits", tagline: "For the groom, the wedding party and the fathers — men & women, from $2,495.", time: "90 min consultation", img: "/images/wedding-couple.jpg" },
  { id: "tuxedos", name: "Tuxedos & Black Tie", tagline: "Satin-lapel dinner suits and velvet jackets for the evening.", time: "60 min consultation", img: "/images/tuxedo-black.jpg" },
];

const PROCESS = [
  { n: "01", title: "The Private Consultation", text: "We meet at your home, office, hotel or our studio. Fabric books, measuring tools and 25+ years of craft — brought to your door." },
  { n: "02", title: "Cloth & Design", text: "Choose from the finest mills — Ariston, Drago, Dormeuil, Fratelli, Holland & Sherry, Loro Piana, Zegna — then shape every detail of the garment." },
  { n: "03", title: "Made by the Finest Houses", text: "Your garment is made in the best Italian tailoring houses, European factories and a top New York atelier — by hand and by the finest tailoring teams, machines and interior materials." },
  { n: "04", title: "The Final Fitting", text: "Reza refines the finished garment on you until it moves exactly the way you do. A garment that lasts decades, not seasons." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const lines = ["Wear Your", "Confidence."];

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 pb-24 pt-20 sm:px-12 lg:grid-cols-12 lg:px-20 lg:pt-28">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#8C6D38]"
            data-testid="hero-overline"
          >
            San Diego · Est. 2018 · By Appointment Only
          </motion.p>
          <h1 className="mt-8 font-serif text-5xl font-light leading-[1.05] tracking-tight text-[#1A1917] sm:text-6xl lg:text-7xl">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.18, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-[#524F4A] sm:text-lg"
            data-testid="hero-subtitle"
          >
            Bespoke &amp; made-to-measure suits, sport coats, shirts, trousers and tuxedos for men
            and women — cut from the finest Italian and English cloth, fitted privately at your
            home, office or our San Diego atelier.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <Link
              to="/book"
              data-testid="hero-book-button"
              className="group inline-flex items-center gap-3 bg-[#1A1917] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#FFFFFF] transition-colors duration-200 hover:bg-[#8C6D38] active:scale-95"
            >
              Book a Fitting
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:+18582694468"
              data-testid="hero-call-button"
              className="inline-flex items-center gap-2 border-b border-[#1A1917] pb-1 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1A1917] transition-colors duration-200 hover:text-[#8C6D38] hover:border-[#8C6D38]"
            >
              <Phone size={13} /> (858) 269-4468
            </a>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: EASE }}
            className="relative overflow-hidden"
          >
            <motion.img
              style={{ y: imgY }}
              src="https://bespokemaestro.com/__l5e/assets-v1/6fe1c611-109c-4b71-970e-f5c121fba625/hero-naples-trendy.jpg"
              alt="Master tailor fitting a bespoke suit jacket"
              className="h-[480px] w-full scale-110 object-cover lg:h-[560px]"
              data-testid="hero-image"
            />
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-[#1A1917]/10" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
            className="absolute -bottom-6 -left-6 border border-[#E5DEC9] bg-white p-6 shadow-[0_24px_60px_-24px_rgba(26,25,23,0.25)]"
            data-testid="hero-stat-card"
          >
            <p className="font-serif text-4xl font-light text-[#1A1917]">25+</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">
              Years of Mastery
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = ["Ariston Napoli", "Drago Biella", "Dormeuil", "Fratelli Tallia di Delfino", "Loro Piana", "Holland & Sherry", "Ermenegildo Zegna"];
  return (
    <div data-testid="fabric-marquee" className="overflow-hidden border-y border-[#E5DEC9] bg-[#FFFFFF] py-5">
      <div className="animate-marquee flex w-max items-center whitespace-nowrap">
        {[...items, ...items].map((m, i) => (
          <span key={i} className="flex items-center font-mono text-[11px] uppercase tracking-[0.3em] text-[#524F4A]">
            <span className="px-8">{m}</span>
            <span className="h-1 w-1 rounded-full bg-[#C59B27]" />
          </span>
        ))}
      </div>
    </div>
  );
}

function Chapter({ index, overline, title, children, img, flip }) {
  return (
    <div className={`grid items-center gap-12 lg:grid-cols-2 ${flip ? "" : ""}`}>
      <Reveal className={flip ? "lg:order-2" : ""}>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8C6D38]">{overline}</p>
        <h3 className="mt-4 font-serif text-3xl font-normal leading-[1.15] tracking-tight text-[#1A1917] sm:text-4xl">
          {title}
        </h3>
        <div className="mt-6 space-y-4 text-base font-light leading-relaxed text-[#524F4A]">{children}</div>
      </Reveal>
      <Reveal delay={0.15} className={flip ? "lg:order-1" : ""}>
        <div className="overflow-hidden">
          <motion.img
            whileHover={{ scale: 1.04 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            src={img}
            alt={title}
            className="h-[420px] w-full object-cover object-top"
            data-testid={`chapter-image-${index}`}
          />
        </div>
      </Reveal>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div data-testid="landing-page" className="bg-[#FFFFFF]">
      <Navbar />
      <Hero />
      <Marquee />

      {/* Chapter I — The Maestro */}
      <section id="heritage" data-testid="chapter-heritage" className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#858078]">Chapter I — The Maestro</p>
        </Reveal>
        <Reveal className="mt-12 max-w-3xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8C6D38]">
            Reza Javanmard — Master Tailor &amp; Founder
          </p>
          <h3 className="mt-4 font-serif text-3xl font-normal leading-[1.15] tracking-tight text-[#1A1917] sm:text-4xl lg:text-5xl">
            Fitted by the Maestro, made by the finest houses in the world
          </h3>
          <div className="mt-8 space-y-5 text-base font-light leading-relaxed text-[#524F4A] sm:text-lg">
            <p>
              With over 25 years devoted to the craft and trained in the European tradition, Reza
              personally takes every measurement and designs every commission with you. Each garment
              is then made in the best Italian tailoring houses, the finest European factories, and
              a top New York atelier — by hand and by the finest tailoring teams, machines and
              materials, down to the interior canvas of every jacket.
            </p>
            <p className="border-l-2 border-[#C59B27] pl-6 font-serif text-xl italic text-[#1A1917]">
              "A truly bespoke garment should make you feel like the best version of yourself — the
              moment you put it on, you should stand a little taller."
            </p>
          </div>
        </Reveal>
      </section>

      {/* Chapter II — The Cloth */}
      <section id="cloth" data-testid="chapter-cloth" className="border-y border-[#E5DEC9] bg-white">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#858078]">Chapter II — The Cloth</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-normal leading-[1.15] tracking-tight text-[#1A1917] sm:text-4xl lg:text-5xl">
              The world's finest fabric houses, at your fingertips
            </h2>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-[#524F4A]">
              Cloth from mills that take a fabric-to-product approach — Ariston, Drago, Dormeuil,
              Fratelli Tallia di Delfino, Holland &amp; Sherry, Loro Piana and Zegna — woven with
              sustainable methods at every step.
            </p>
          </Reveal>
          <div className="mt-16 grid gap-px overflow-hidden border border-[#E5DEC9] bg-[#E5DEC9] sm:grid-cols-2 lg:grid-cols-3">
            {MILLS.map((mill, i) => (
              <motion.div
                key={mill.name}
                data-testid={`fabric-card-${mill.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: EASE }}
                whileHover={{ y: -4 }}
                className="group bg-white p-8 transition-colors duration-300 hover:bg-[#FFFFFF]"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C6D38]">{mill.origin}</p>
                <h3 className="mt-3 font-serif text-2xl font-medium text-[#1A1917]">{mill.name}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#524F4A]">{mill.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="border-b border-[#E5DEC9] bg-white">
        <p className="mx-auto max-w-7xl px-6 pb-14 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-[#858078] sm:px-12 lg:px-20">
          For shirting — Albini · Alumo · Canclini · Thomas Mason
        </p>
      </div>

      {/* Chapter III — Commissions */}
      <section id="services" data-testid="chapter-services" className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#858078]">Chapter III — The Commissions</p>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl font-normal leading-[1.15] tracking-tight text-[#1A1917] sm:text-4xl lg:text-5xl">
            Garments built around your life, season by season
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-6">
          {SERVICES_LIST.map((s, i) => (
            <motion.div
              key={s.id}
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: EASE }}
              whileHover={{ y: -4 }}
              className={`group border border-[#E5DEC9] bg-white ${i < 2 ? "lg:col-span-3" : "lg:col-span-2"}`}
            >
              <div className="relative overflow-hidden">
                <img
                  src={s.img}
                  alt={s.name}
                  className={`aspect-square w-full transition-transform duration-700 ease-out group-hover:scale-105 ${
                    s.contain ? "bg-white object-contain p-8" : "object-cover object-top"
                  }`}
                />
                {s.cloth && (
                  <p className="absolute bottom-0 left-0 border-r border-t border-[#E5DEC9] bg-white/95 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">
                    Cloth — {s.cloth}
                  </p>
                )}
              </div>
              <div className="p-8">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C6D38]">{s.time}</p>
                  <Clock size={12} className="text-[#C59B27]" />
                </div>
                <h3 className="mt-3 font-serif text-2xl font-medium text-[#1A1917]">{s.name}</h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-[#524F4A]">{s.tagline}</p>
                <Link
                  to={`/book?service=${s.id}`}
                  data-testid={`service-book-${s.id}`}
                  className="mt-6 inline-flex items-center gap-2 border-b border-[#1A1917] pb-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#1A1917] transition-colors duration-200 hover:border-[#8C6D38] hover:text-[#8C6D38]"
                >
                  Reserve <ArrowRight size={12} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Chapter IV — The Process */}
      <section id="process" data-testid="chapter-process" className="border-y border-[#5F5F5F] bg-[#6B6B6B] text-[#FFFFFF]">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#C59B27]">Chapter IV — The Process</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-normal leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl">
              From first measurement to final pressing
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1}>
                <p className="font-serif text-5xl font-light text-[#C59B27]">{p.n}</p>
                <h3 className="mt-4 font-serif text-xl font-medium">{p.title}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#E8E8E8]">{p.text}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-16 border-t border-[#FFFFFF]/10 pt-8">
            <p className="max-w-3xl text-sm font-light leading-relaxed text-[#E8E8E8]">
              Made-to-measure garments need four to eight weeks plus fittings; full bespoke needs
              eight to twelve. For weddings, begin eight to twelve weeks before the day — we
              coordinate the groom, the wedding party and the fathers so the group reads as one.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Visit */}
      <section id="visit" data-testid="chapter-visit" className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#858078]">The Atelier Comes to You</p>
            <h2 className="mt-4 font-serif text-3xl font-normal leading-[1.15] tracking-tight text-[#1A1917] sm:text-4xl lg:text-5xl">
              Your tailor is a phone call away — not a trunk show
            </h2>
            <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-[#524F4A]">
              We are your neighborhood master tailor. Private consultations at your home, office,
              hotel or our studio — serving La Jolla, Del Mar, Rancho Bernardo, Carmel Valley,
              Coronado, Downtown San Diego and Orange County.
            </p>
            <div className="mt-8 space-y-3 text-sm font-light text-[#524F4A]">
              <p className="flex items-center gap-3"><MapPin size={15} className="text-[#8C6D38]" /> San Diego &amp; Orange County, California</p>
              <p className="flex items-center gap-3"><Phone size={15} className="text-[#8C6D38]" /> (858) 269-4468</p>
              <p className="flex items-center gap-3"><Clock size={15} className="text-[#8C6D38]" /> By appointment only — Monday through Saturday</p>
            </div>
            <Link
              to="/book"
              data-testid="visit-book-button"
              className="mt-10 inline-flex items-center gap-3 bg-[#1A1917] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#FFFFFF] transition-colors duration-200 hover:bg-[#8C6D38] active:scale-95"
            >
              Book Your Fitting <ArrowRight size={14} />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden">
              <motion.img
                whileHover={{ scale: 1.04 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                src="https://bespokemaestro.com/assets/atelier-workbench-real-Cdz5KAxC.jpg"
                alt="Bespoke Maestro atelier"
                className="h-[440px] w-full object-cover"
                data-testid="visit-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
